/**
 * TenantSwitcher — dropdown สลับบริษัทสำหรับเจ้าของ/ผู้ที่อยู่หลายบริษัท.
 * บริษัทเดียว → แสดงชื่อเฉยๆ (ไม่มี dropdown). >1 → กดสลับได้.
 * port จาก PayPilot (profile แบบ nested {tenant:{id,name}} ของ eval).
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Building2 } from 'lucide-react';
import { useAuthStore } from '../lib/store';
import { useMyTenants, useSwitchTenant } from '../hooks/useAuth';
import { useToast } from './ui/toast';

export function TenantSwitcher() {
  const profile = useAuthStore((s) => s.profile);
  const { data: memberships } = useMyTenants();
  const switchT = useSwitchTenant();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const currentName = profile?.tenant?.name ?? '—';
  const currentTenantId = profile?.tenant?.id;
  const hasMultiple = (memberships?.length ?? 0) > 1;

  if (!hasMultiple) {
    return <div className="mt-1 text-xs text-gray-500 truncate" title={currentName}>{currentName}</div>;
  }

  async function handleSwitch(tenantId: string) {
    if (tenantId === currentTenantId) { setOpen(false); return; }
    try {
      await switchT.mutateAsync(tenantId);
      setOpen(false);
      toast.success('สลับบริษัทแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สลับบริษัทไม่สำเร็จ');
    }
  }

  return (
    <div className="relative mt-1.5" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switchT.isPending}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-2.5 py-1.5 text-left disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span className="text-xs font-medium text-gray-700 truncate">{currentName}</span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="listbox" className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          {memberships!.map((m) => {
            const active = m.tenant_id === currentTenantId;
            return (
              <button
                key={m.tenant_id}
                role="option"
                aria-selected={active}
                onClick={() => handleSwitch(m.tenant_id)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 text-left text-xs hover:bg-gray-50 ${active ? 'bg-brand-50' : ''}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{m.tenant_name}</div>
                  <div className="text-[10px] text-gray-500 truncate">{m.role}{m.tenant_code ? ` · ${m.tenant_code}` : ''}</div>
                </div>
                {active && <Check className="w-3.5 h-3.5 text-brand-600 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
